import { Box, Button, styled } from '@mui/material';
import { ChangeEvent, useCallback, useRef } from 'react';
import { ColoredAvatar } from 'src/components/ColoredAvatar/colored-avatar';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const AVATAR_SIZE = '5rem';

const Wrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  marginBottom: theme.spacing(1),
}));

const StyledColoredAvatar = styled(ColoredAvatar)(() => ({
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  fontSize: '2rem',
}));

const HiddenInput = styled('input')({
  display: 'none',
});

export interface AvatarUploaderProps {
  name: string;
  imageUrl?: string;
  onFileChange: (file: File) => void;
}

export const AvatarUploader = ({ name, imageUrl, onFileChange }: AvatarUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handlePickClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (file) {
        onFileChange(file);
      }
    },
    [onFileChange],
  );

  return (
    <Wrapper>
      <StyledColoredAvatar name={name} imageUrl={imageUrl} />
      <Button
        type="button"
        size="small"
        onClick={handlePickClick}
        startIcon={<DynamicIcon name="edit" sx={{ fontSize: '1rem' }} />}
      >
        {imageUrl ? 'Change photo' : 'Upload photo'}
      </Button>
      <HiddenInput
        ref={inputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        onChange={handleChange}
      />
    </Wrapper>
  );
};
